import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LogOut, Download, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { downloadResourceUrl } from "../lib/api";
import { Seo } from "../components/Seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const signIn = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-24 text-[#6E7784]">
        Checking session…
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="account-signin-page" className="max-w-[900px] mx-auto px-6 lg:px-10 py-24">
        <Seo title="Account" description="Sign in to your Peptide Labs N.I. account." noindex />
        <div className="border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] p-12">
          <div className="label-mono">Sign in</div>
          <h1 className="mt-4 text-3xl lg:text-4xl tracking-tight text-white font-medium">
            Access your research account.
          </h1>
          <p className="mt-4 text-[#B7BCC7] max-w-md leading-relaxed">
            Sign in with Google to unlock the <span className="text-white">Advanced Peptide Protocols</span> PDF,
            save orders, track shipments, and manage your certificate-of-analysis library. Guest checkout
            remains available without an account.
          </p>
          <button
            onClick={signIn}
            data-testid="account-google-signin-btn"
            className="mt-8 btn-primary inline-flex items-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.5 18.9 12 24 12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13-5l-6-5.1c-2 1.4-4.4 2.1-7 2.1-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6 5.1C40.9 34.9 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Sign in with Google <ArrowRight size={16} />
          </button>
          <div className="mt-6 label-mono">Guest checkout available at /cart</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-page" className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <div className="label-mono">Account</div>
      <div className="mt-6 flex items-center gap-5">
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-16 h-16 rounded-full border border-[#2A2A2A]"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-16 h-16 rounded-full border border-[#2A2A2A] bg-[#171717] grid place-items-center text-white text-xl">
            {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <div className="text-white text-2xl tracking-tight font-medium">{user.name || user.email}</div>
          <div className="text-[#6E7784] text-sm mt-1" data-testid="account-email">{user.email}</div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        <button
          onClick={() => { window.location.href = downloadResourceUrl("advanced-peptide-protocols"); toast.success("Preparing your PDF…"); }}
          data-testid="account-download-protocols-btn"
          className="text-left p-6 border border-[#1565FF]/40 rounded-2xl bg-[#0e0e0e] hover:bg-[#111] hover:border-[#1565FF] transition-colors shadow-lab-glow"
        >
          <div className="flex items-center justify-between">
            <FileText className="text-[#1565FF]" size={20} />
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1565FF]/15 border border-[#1565FF]/40 text-[#1565FF] text-[10px] tracking-widest font-mono">
              <Download size={10} /> READY
            </span>
          </div>
          <div className="label-mono mt-6">Featured resource</div>
          <div className="mt-2 text-white text-lg tracking-tight font-medium">Advanced Peptide Protocols</div>
          <div className="mt-1 text-[#B7BCC7] text-sm">PDF · 8 pages · Rev 2026.02</div>
          <div className="mt-4 inline-flex items-center gap-1 text-[#1565FF] text-sm">
            Download PDF <ArrowRight size={14} />
          </div>
        </button>

        <div className="p-6 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e]">
          <div className="label-mono">Orders</div>
          <div className="mt-4 text-white text-2xl font-mono">—</div>
          <p className="mt-2 text-[#B7BCC7] text-sm">Order history is coming soon.</p>
        </div>
        <div className="p-6 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e]">
          <div className="label-mono">Shipping addresses</div>
          <div className="mt-4 text-white text-2xl font-mono">—</div>
          <p className="mt-2 text-[#B7BCC7] text-sm">Saved during your first order.</p>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <Link to="/products" className="btn-primary inline-flex items-center gap-2">
          Continue Research <ArrowRight size={16} />
        </Link>
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="account-logout-btn"
          className="btn-ghost inline-flex items-center gap-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

export { signIn };
